import { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Shield, Activity, FileCheck, Mail, RefreshCw } from 'lucide-react';

/**
 * AdminOps — one screen that surfaces the four Cluster-5 observability
 * feeds populated by iter64/65/66:
 *   1. Security → recent-lockouts       (login_attempts aggregate)
 *   2. Audit log                        (audit_log, filterable)
 *   3. Legal acceptances                (legal_acceptances)
 *   4. Emails sent                      (retailer_kickoff_log; extendable)
 *
 * Kept dense on purpose — this is a monitoring cockpit, not a marketing
 * page.
 */
export default function AdminOps({ user, onLogout }) {
  const [tab, setTab] = useState('security');

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-ops-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ops &amp; Observability</h1>
          <p className="text-sm text-slate-500 mt-1">
            Security lockouts, audit trail, legal acceptances, and outbound email log — one dashboard.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 max-w-2xl">
            <TabsTrigger value="security" data-testid="ops-tab-security">
              <Shield className="w-3 h-3 mr-1" /> Security
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="ops-tab-audit">
              <Activity className="w-3 h-3 mr-1" /> Audit
            </TabsTrigger>
            <TabsTrigger value="legal" data-testid="ops-tab-legal">
              <FileCheck className="w-3 h-3 mr-1" /> Legal
            </TabsTrigger>
            <TabsTrigger value="emails" data-testid="ops-tab-emails">
              <Mail className="w-3 h-3 mr-1" /> Emails
            </TabsTrigger>
          </TabsList>

          <TabsContent value="security" className="mt-4">
            <SecurityPanel />
          </TabsContent>
          <TabsContent value="audit" className="mt-4">
            <AuditPanel />
          </TabsContent>
          <TabsContent value="legal" className="mt-4">
            <LegalPanel />
          </TabsContent>
          <TabsContent value="emails" className="mt-4">
            <EmailsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

/* ------------------------------- Panels ------------------------------- */

function useEndpoint(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(path);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [path]);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, error, refresh };
}

function RefreshBtn({ onClick, loading, testid }) {
  return (
    <Button size="sm" variant="ghost" onClick={onClick} disabled={loading} data-testid={testid}>
      <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
      Refresh
    </Button>
  );
}

function SecurityPanel() {
  const { data, loading, error, refresh } = useEndpoint('/admin/security/recent-lockouts?hours=24');
  const rows = data?.rows || [];
  return (
    <Card data-testid="ops-security-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Recent auth-lockout patterns</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Failed attempts by (ip, email) in the last 24 hours. Rows with ≥5 attempts hit the rate-limit.
          </p>
        </div>
        <RefreshBtn onClick={refresh} loading={loading} testid="ops-security-refresh" />
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && rows.length === 0 && <p className="text-sm text-slate-500">No failed attempts in the window. 👍</p>}
        <table className="w-full text-sm">
          <tbody>
            {rows.map(r => (
              <tr key={r.identifier} className="border-b border-slate-100">
                <td className="py-2 font-mono text-xs">{r.identifier}</td>
                <td className="py-2 text-right">
                  <Badge className={r.likely_locked ? 'bg-red-500' : 'bg-slate-400'}>
                    {r.attempts}
                  </Badge>
                </td>
                <td className="py-2 text-right text-xs text-slate-500 pl-4">
                  {new Date(r.last_seen).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function AuditPanel() {
  const [action, setAction] = useState('');
  const [target, setTarget] = useState('');
  const queryStr = [
    'limit=100',
    action && `action=${encodeURIComponent(action)}`,
    target && `target_type=${encodeURIComponent(target)}`,
  ].filter(Boolean).join('&');
  const { data, loading, error, refresh } = useEndpoint(`/admin/audit-log?${queryStr}`);
  const rows = data?.rows || [];
  return (
    <Card data-testid="ops-audit-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Admin audit trail</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Every admin mutation, newest first. Filter by action or target_type.</p>
        </div>
        <RefreshBtn onClick={refresh} loading={loading} testid="ops-audit-refresh" />
      </CardHeader>
      <CardContent className="max-h-[60vh] overflow-y-auto">
        <div className="flex gap-2 items-center pb-3 border-b border-slate-100 mb-3" data-testid="ops-audit-filter-row">
          <Input
            placeholder="Filter action (e.g. retailer.approve_go_live)"
            value={action}
            onChange={e => setAction(e.target.value)}
            className="h-9 w-72 text-sm"
            data-testid="ops-audit-filter-action"
          />
          <Input
            placeholder="Filter target_type"
            value={target}
            onChange={e => setTarget(e.target.value)}
            className="h-9 w-40 text-sm"
            data-testid="ops-audit-filter-target"
          />
          {(action || target) && (
            <Button
              size="sm" variant="ghost"
              onClick={() => { setAction(''); setTarget(''); }}
              data-testid="ops-audit-filter-clear"
            >
              Clear
            </Button>
          )}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && rows.length === 0 && <p className="text-sm text-slate-500">No matching audit rows.</p>}
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 text-left">
            <tr>
              <th className="py-1">When</th><th className="py-1">Who</th>
              <th className="py-1">Action</th><th className="py-1">Target</th><th className="py-1">IP</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="py-1 text-xs">{new Date(r.timestamp).toLocaleString()}</td>
                <td className="py-1 text-xs">{r.actor_email}</td>
                <td className="py-1 text-xs font-mono">{r.action}</td>
                <td className="py-1 text-xs">{r.target_type}:{(r.target_id || '').slice(0,8)}</td>
                <td className="py-1 text-xs font-mono text-slate-500">{r.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function LegalPanel() {
  const { data, loading, error, refresh } = useEndpoint('/legal/history?limit=200');
  const rows = data?.rows || [];
  return (
    <Card data-testid="ops-legal-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Legal document acceptances</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Every user acceptance of a legal doc — powers compliance reports.
          </p>
        </div>
        <RefreshBtn onClick={refresh} loading={loading} testid="ops-legal-refresh" />
      </CardHeader>
      <CardContent className="max-h-[60vh] overflow-y-auto">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && rows.length === 0 && <p className="text-sm text-slate-500">No acceptances yet.</p>}
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 text-left">
            <tr><th>When</th><th>User</th><th>Doc</th><th>Version</th><th>IP</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="py-1 text-xs">{new Date(r.accepted_at).toLocaleString()}</td>
                <td className="py-1 text-xs">{r.user_email}</td>
                <td className="py-1 text-xs font-mono">{r.doc_id}</td>
                <td className="py-1 text-xs">v{r.version}</td>
                <td className="py-1 text-xs font-mono text-slate-500">{r.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function EmailsPanel() {
  const { data, loading, error, refresh } = useEndpoint('/admin/retailers/emails/recent?limit=100');
  const rows = data?.rows || [];
  return (
    <Card data-testid="ops-emails-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Recent outbound emails</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Kickoff, verification and reset emails. Currently tracks retailer_kickoff_log; more collections merge in as they land.
          </p>
        </div>
        <RefreshBtn onClick={refresh} loading={loading} testid="ops-emails-refresh" />
      </CardHeader>
      <CardContent className="max-h-[60vh] overflow-y-auto">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && rows.length === 0 && <p className="text-sm text-slate-500">No sends yet.</p>}
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 text-left">
            <tr><th>When</th><th>To</th><th>Kind</th><th>Status</th><th>Sent by</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1 text-xs">{new Date(r.sent_at).toLocaleString()}</td>
                <td className="py-1 text-xs">{r.retailer_email}</td>
                <td className="py-1 text-xs font-mono">{r.kind}</td>
                <td className="py-1 text-xs">
                  <Badge className={r.status === 'sent' ? 'bg-emerald-500' : r.status === 'failed' ? 'bg-red-500' : 'bg-slate-400'}>
                    {r.status}
                  </Badge>
                </td>
                <td className="py-1 text-xs text-slate-500">{r.sent_by_email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
